import { ChangeDetectionStrategy, Component, Signal, ViewChild, computed, effect, inject, input, model, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import { ComponentsModule, DrawerComponent, LodashService, MessageType } from '../../../../lib';
import { RoomType } from '../../models';
import { BuildingService } from '../../services';
import { RoomByRoomCommon } from '../../common';
import { ContextModelState, QuestionFlowComponent, RBR_CONFIGURATION } from '../../../question-flow';

@Component({
    selector: 'ca-rbr-room-editor',
    templateUrl: './room-editor.component.html',
    styleUrls: ['./room-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, ComponentsModule, MatDividerModule, QuestionFlowComponent]
})
export class RoomEditorComponent {
    // Question Flow mock configuration
    public readonly qfEditRoomMock = RBR_CONFIGURATION as any;
    public readonly roomTypes = Object.values(RoomType);
    public readonly messageTypes = MessageType;

    private readonly buildingService = inject(BuildingService);

    private currentContextModelState: ContextModelState = {};
    public selectedRoomId = input.required<string>();
    public isDrawerVisible = signal(false);
    public isRoomNameEdited = signal(false);
    public isConfirmPending = signal(false);
    public hasConfirmError = signal(false);
    public hasQFContextChanged = signal(false);
    public hasFormChanged = signal(false);
    public isQuestionFlowValid = signal(true);
    public questionFlowContext = signal<ContextModelState>({});
    public innerLoading = this.buildingService.getInnerLoading();
    public hasError = this.buildingService.getInnerError();
    private readonly building = this.buildingService.getBuilding();
    private readonly hasLoadingStarted = signal(false);

    @ViewChild('drawer') drawer!: DrawerComponent;

    readonly form = new FormGroup({
        name: new FormControl<string | null>(null, {
            validators: [RoomByRoomCommon.trimmedRequired]
        })
    });

    private readonly roomName = computed(() => {
        const id = this.selectedRoomId();
        const building = this.building();
        if (!id || !building?.rooms?.[id]) {
            return '';
        }

        return building.rooms[id].name;
    });

    private readonly roomDataEffect = effect(() => {
        this.initRoomData();
    });

    private readonly pendingSaveEffect = effect(() => {
        if (!this.isConfirmPending()) {
            return;
        }

        if (this.innerLoading()) {
            this.hasLoadingStarted.set(true);
            return;
        }

        if (!this.hasLoadingStarted()) {
            return;
        }

        if (this.hasError()) {
            this.isConfirmPending.set(false);
            this.hasLoadingStarted.set(false);
            this.hasConfirmError.set(true);
            return;
        }

        this.hasConfirmError.set(false);
        this.isConfirmPending.set(false);
        this.hasLoadingStarted.set(false);
        this.isDrawerVisible.set(false);
        this.drawer.close();
    });

    constructor() {
        this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            const currentFormState = this.form.getRawValue();
            const initialFormState = this.getInitialFormState();
            this.hasFormChanged.set(!LodashService.isEqual(currentFormState, initialFormState));
        });
    }

    openDrawer(): void {
        this.onNameCancel();
        this.isConfirmPending.set(false);
        this.hasLoadingStarted.set(false);
        this.hasConfirmError.set(false);
        this.hasQFContextChanged.set(false);
        this.hasFormChanged.set(false);
        this.isQuestionFlowValid.set(true);
        this.isDrawerVisible.set(true);
        this.drawer.open();
    }

    onVisibleChange(isVisible: boolean): void {
        this.isDrawerVisible.set(isVisible);

        if (isVisible) {
            return;
        }

        this.isConfirmPending.set(false);
        this.hasLoadingStarted.set(false);
        this.hasConfirmError.set(false);
        this.hasQFContextChanged.set(false);
        this.hasFormChanged.set(false);
        this.isQuestionFlowValid.set(true);
        this.resetFormToInputs();
        this.resetQFToInputs();
    }

    onConfirm(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const value = this.form.getRawValue();
        const roomName = value.name?.trim() ?? '';
        if (!roomName) {
            return;
        }

        const selectedRoomId = this.selectedRoomId();
        const building = this.currentContextModelState.project.building;
        if (building?.rooms?.[selectedRoomId]) {
            building.rooms[selectedRoomId].name = roomName;
        }

        this.hasConfirmError.set(false);
        this.isConfirmPending.set(true);
        this.buildingService.updateBuilding(building);
    }

    onClose(): void {
        this.isConfirmPending.set(false);
        this.hasLoadingStarted.set(false);
        this.hasConfirmError.set(false);
        this.form.reset();
        this.isDrawerVisible.set(false);
        this.drawer.close();
    }

    tryToClose(): void {
        if (this.hasQFContextChanged() || this.hasFormChanged()) {
            return;
        }

        this.onClose();
    }

    onNameEdit(): void {
        this.isRoomNameEdited.set(true);
    }

    onNameCancel(): void {
        this.isRoomNameEdited.set(false);
        this.form.patchValue({ name: this.roomName() });
    }

    onNameSave(): void {
        this.isRoomNameEdited.set(false);
    }

    onValidityChange(isValid: boolean): void {
        this.isQuestionFlowValid.set(isValid);
    }

    onContextChange(context: ContextModelState): void {
        this.currentContextModelState = context;
        const initialBuildingData = this.building();
        const currentBuildingData = context.project?.building;
        this.hasQFContextChanged.set(!LodashService.isEqual(currentBuildingData, initialBuildingData));
    }

    private resetFormToInputs(): void {
        this.form.reset(
            {
                name: this.roomName() ?? ''
            },
            { emitEvent: false }
        );
        this.form.markAsPristine();
        this.form.markAsUntouched();
    }

    private resetQFToInputs(): void {
        this.initRoomData();
    }

    private initRoomData(): void {
        const selectedRoomId = this.selectedRoomId();
        if (!this.form) {
            return;
        }

        this.currentContextModelState = {
            project: {
                building: this.building()
            },
            inputs: {
                selectedRoomId
            }
        };

        this.questionFlowContext.set({
            project: {
                building: this.building()
            },
            inputs: {
                selectedRoomId
            }
        });

        this.form.patchValue(this.getInitialFormState());
    }

    private getInitialFormState(): { name: string } {
        const name = this.roomName();

        return { name };
    }
}
